import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Stack,
} from "@mui/material";
import { Button, Dialog, Typography } from "@platform/design-system";
import { useEffect, useState } from "react";

import type { HistoryCarrierOption } from "../utils/quoteHistoryCarriers";

interface QuoteHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Carriers with at least one requote, scoped to the current coverage. */
  carriers: HistoryCarrierOption[];
  /**
   * Whether the carriers are still being loaded. Distinct from an empty list: reporting
   * "no carriers" while the answer is unknown states something false.
   */
  isLoadingCarriers: boolean;
  /** Carrier ids currently applied to the history view (to preselect on open). */
  selectedCarrierIds: string[];
  /** Applies the selection and enters the history view. */
  onView: (carrierIds: string[]) => void;
}

const optionLabel = (option: HistoryCarrierOption): string =>
  `${option.name} (${option.historyCount})`;

/**
 * Quote History carrier-selection dialog (ADR-031). Opened from the "View Quote
 * History" toggle: the analyst picks one or more carriers (each labelled with
 * its requote count) and "View" enters the history view filtered to them.
 *
 * The carriers are listed inline rather than inside a select. A dropdown's popup
 * renders over the dialog's own actions, so the analyst could not see the button
 * they were selecting toward — and the list is short enough (only carriers with
 * history appear) that hiding it behind a control bought nothing.
 */
export const QuoteHistoryDialog: React.FC<QuoteHistoryDialogProps> = ({
  open,
  onClose,
  carriers,
  isLoadingCarriers,
  selectedCarrierIds,
  onView,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Sync the local selection with what's applied whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(
      carriers
        .filter((c) => selectedCarrierIds.includes(c.id))
        .map((c) => c.id),
    );
  }, [open, carriers, selectedCarrierIds]);

  const toggleCarrier = (carrierId: string): void =>
    setSelectedIds((previous) =>
      previous.includes(carrierId)
        ? previous.filter((id) => id !== carrierId)
        : [...previous, carrierId],
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Quote History"
      maxWidth="xs"
      actions={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={selectedIds.length === 0}
            onClick={() => onView(selectedIds)}
          >
            View
          </Button>
        </>
      }
    >
      <Stack spacing={2} direction="column" data-testid="quote-history-dialog">
        <Typography variant="body-medium" color="textSecondary">
          Please select one or more carriers to view your quote history
        </Typography>
        {isLoadingCarriers ? (
          <Stack spacing={1} aria-busy aria-label="Loading carriers">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="text" height={32} />
            ))}
          </Stack>
        ) : carriers.length === 0 ? (
          <Typography variant="body-medium" color="textSecondary">
            No carriers with quote history
          </Typography>
        ) : (
          // Capped so a carrier-heavy page scrolls the list rather than pushing the
          // actions off the dialog — the problem the inline list exists to avoid.
          <FormGroup
            sx={{ maxHeight: 320, overflowY: "auto", flexWrap: "nowrap" }}
          >
            {carriers.map((carrier) => (
              <FormControlLabel
                key={carrier.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(carrier.id)}
                    onChange={() => toggleCarrier(carrier.id)}
                  />
                }
                label={
                  <Typography variant="body-medium">
                    {optionLabel(carrier)}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        )}
      </Stack>
    </Dialog>
  );
};
